import { Alert, AlertIcon, Input, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import DateBlurInput from '../../../components/DateBlurInput'
import {
  FILTER_CONTROL_SIZE,
  FilterBar,
  FilterDateRange,
  FilterField,
  FilterSearch,
  FilterSelect
} from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import ShortText from '../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { useUser } from '../../../contexts/UserContext'
import { formatCurrency, formatDate, formatIsoDate, useAppToast, useHasRole } from '../../../helpers'
import {
  IPrizeSummaryItem,
  ISalesReceipt,
  paymentMethodLabel,
  PAYMENT_METHOD_LABELS,
  RECEIPT_REFERENCE_LABELS,
  RECEIPT_TYPE_LABELS,
  SALE_CONDITION_LABELS,
  SalesReceiptService
} from '../../../services/SalesReceipt/SalesReceiptService'
import SalesReceiptEditModal from './SalesReceiptEditModal'
import SalesReceiptPrizeSummary from './SalesReceiptPrizeSummary'
import SalesReceiptViewModal from './SalesReceiptViewModal'

const formatValue = (value: string | null) => (value === null ? '-' : formatCurrency(Number(value)))

const formatPrize = (prize: number | null) => (!prize ? '-' : formatCurrency(prize))

const referenceCell = (receipt: ISalesReceipt): string => {
  const referenceLabel = receipt.receipt_reference ? RECEIPT_REFERENCE_LABELS[receipt.receipt_reference] : null
  const onlineLabel = receipt.courses?.length ? 'Curso online' : null

  if (referenceLabel && onlineLabel) return `${referenceLabel} - ${onlineLabel}`
  return referenceLabel ?? onlineLabel ?? '-'
}

/** `length` fica de fora: é o tamanho de página escolhido no Paginator, não um filtro. */
const INITIAL_FILTERS = {
  search: '',
  receiptType: '',
  saleCondition: '',
  paymentMethod: '',
  receiptReference: '',
  dateStart: '',
  dateEnd: '',
  prizePaymentMonth: ''
}

const toOptions = (labels: Record<string, string>) => Object.entries(labels).map(([value, label]) => ({ value, label }))

function SalesReceiptsPage() {
  const service = new SalesReceiptService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const { id: userId, commercialConsultantId } = useUser()
  const permissions = {
    edit: hasRole('edit_sales_receipts'),
    editOwn: hasRole('edit_own_sales_receipts'),
    delete: hasRole('delete_sales_receipts'),
    editPrizePaymentDate: hasRole('edit_sales_receipts_prize_payment_date')
  }

  // Quem edita todos, edita todos; quem só tem a permissão restrita edita apenas os
  // comprovantes do consultor que a sua conta representa (ABRA-140). O backend valida.
  // Contract extensions belong to the account itself, not to a linked consultant (ABRA-388).
  const isOwnReceipt = (receipt: ISalesReceipt) =>
    receipt.receipt_type === 'contract_extension'
      ? userId !== null && receipt.user_id === Number(userId)
      : commercialConsultantId !== null && receipt.commercial_consultant_id === commercialConsultantId
  const canEditReceipt = (receipt: ISalesReceipt) => permissions.edit || (permissions.editOwn && isOwnReceipt(receipt))

  const [receipts, setReceipts] = useState<ISalesReceipt[]>([])
  const [prizeSummary, setPrizeSummary] = useState<IPrizeSummaryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({ length: DEFAULT_PAGE_SIZE, ...INITIAL_FILTERS })
  const isDirty = (Object.keys(INITIAL_FILTERS) as (keyof typeof INITIAL_FILTERS)[]).some(
    (key) => filters[key] !== INITIAL_FILTERS[key]
  )
  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  // Limpar não pode devolver o tamanho de página ao padrão: quem escolheu 100 por página
  // continua com 100 depois de zerar os filtros.
  const clearFilters = () => updateFilters(INITIAL_FILTERS)

  const [viewingId, setViewingId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null })

  const fetchReceipts = async () => {
    setLoading(true)
    setLoadError(false)
    // Clear stale prize totals up front so a month change doesn't briefly show
    // the previous month's summary while the table is still reloading.
    setPrizeSummary([])
    try {
      const data = await service.list({
        search: filters.search,
        receipt_type: filters.receiptType,
        sale_condition: filters.saleCondition,
        payment_method: filters.paymentMethod,
        receipt_reference: filters.receiptReference,
        date: { start: filters.dateStart, end: filters.dateEnd },
        prize_payment_month: filters.prizePaymentMonth,
        page,
        page_size: filters.length
      })
      setTotalPages(Math.ceil(data.recordsFiltered / filters.length) || 1)
      setReceipts(data.data)
      setPrizeSummary(data.prize_summary || [])
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os comprovantes', error)
    } finally {
      setLoading(false)
    }
  }

  // A busca já chega debounced do `FilterSearch`, então não há debounce próprio aqui — e com
  // `updateFilters` voltando para a página 1, as duas mudanças caem numa renderização só e
  // este efeito busca uma vez (antes eram duas requisições na montagem, escondidas pelo atraso).
  useEffect(() => {
    fetchReceipts()
  }, [page, filters])

  const handlePrizePaymentDateUpdate = async (receipt: ISalesReceipt, value: string) => {
    try {
      await service.updatePrizePaymentDate(receipt.id, value || null)
      toast.success('Data de pagamento do prêmio salva')
      fetchReceipts()
    } catch (e) {
      toast.error('Erro ao salvar data de pagamento do prêmio', e)
      // Re-throw so DateBlurInput keeps the field editable for retry.
      throw e
    }
  }

  const handleDelete = async () => {
    if (!deleteDialog.id) return
    try {
      await service.delete(deleteDialog.id)
      toast.success('Comprovante removido com sucesso')
      fetchReceipts()
    } catch (e) {
      toast.error('Erro ao remover comprovante', e)
    } finally {
      setDeleteDialog({ open: false, id: null })
    }
  }

  return (
    <>
      <PageTitle title="Comprovantes de Vendas" description="Comprovantes enviados pelos consultores comerciais" />

      <FilterBar onClear={clearFilters} isDirty={isDirty}>
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Consultor ou matrícula"
        />
        <FilterSelect
          label="Tipo"
          value={filters.receiptType}
          onChange={(receiptType) => updateFilters({ receiptType })}
          options={toOptions(RECEIPT_TYPE_LABELS)}
        />
        <FilterSelect
          label="Condição da Venda"
          emptyLabel="Todas"
          value={filters.saleCondition}
          onChange={(saleCondition) => updateFilters({ saleCondition })}
          options={toOptions(SALE_CONDITION_LABELS)}
        />
        <FilterSelect
          label="Forma de Pagamento"
          emptyLabel="Todas"
          value={filters.paymentMethod}
          onChange={(paymentMethod) => updateFilters({ paymentMethod })}
          options={toOptions(PAYMENT_METHOD_LABELS)}
        />
        <FilterSelect
          label="Referente a"
          value={filters.receiptReference}
          onChange={(receiptReference) => updateFilters({ receiptReference })}
          options={toOptions(RECEIPT_REFERENCE_LABELS)}
        />
        <FilterDateRange
          label="Data de envio"
          value={{ start: filters.dateStart, end: filters.dateEnd }}
          onChange={(date) => updateFilters({ dateStart: date.start, dateEnd: date.end })}
        />
        <FilterField label="Mês Pagto Prêmio">
          <Input
            type="month"
            size={FILTER_CONTROL_SIZE}
            value={filters.prizePaymentMonth}
            onChange={(e) => updateFilters({ prizePaymentMonth: e.target.value })}
          />
        </FilterField>
      </FilterBar>

      {filters.prizePaymentMonth && <SalesReceiptPrizeSummary items={prizeSummary} />}

      {permissions.editOwn && !permissions.edit && commercialConsultantId === null && (
        <Alert status="info" mb="3" borderRadius="md">
          <AlertIcon />
          Você só pode editar os comprovantes de Extensão Contratual em que foi selecionado. Para editar os seus
          comprovantes de Matrícula / Rematrícula, peça a um administrador para vincular sua conta a um consultor
          comercial.
        </Alert>
      )}

      <TableContainer>
        <Table padding="0" mt="2">
          <Thead>
            <Tr>
              <Th>Data Envio</Th>
              <Th>ID</Th>
              <Th>Tipo</Th>
              <Th>Consultor</Th>
              <Th>Nº Matrícula</Th>
              <Th>Referente a</Th>
              <Th>Condição</Th>
              <Th>Pagamento</Th>
              <Th>Valor</Th>
              <Th>Prêmio</Th>
              <Th>Data Pagto Prêmio</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={12} />
            ) : loadError ? (
              <TableErrorRow colSpan={12} entity="comprovantes" onRetry={fetchReceipts} />
            ) : receipts.length === 0 ? (
              <TableEmptyRow colSpan={12} entity="comprovante" />
            ) : (
              receipts.map((receipt) => (
                <Tr key={receipt.id}>
                  <Td>{formatDate(receipt.created_at)}</Td>
                  <Td>#{receipt.id}</Td>
                  <Td>{RECEIPT_TYPE_LABELS[receipt.receipt_type]}</Td>
                  <Td>{receipt.consultant_name}</Td>
                  <Td>
                    {/* Online é um "Nº da Fatura / Pedido" de texto livre, e presencial pode
                        trazer várias matrículas: sem truncar, um valor comprido rolava a
                        tabela inteira para o lado (o TableContainer é nowrap). */}
                    {receipt.registration_number?.length ? (
                      <ShortText text={receipt.registration_number.join(', ')} beforeChars={20} afterChars={0} />
                    ) : (
                      '-'
                    )}
                  </Td>
                  <Td>
                    <ShortText text={referenceCell(receipt)} beforeChars={25} afterChars={0} />
                  </Td>
                  <Td>{receipt.sale_condition ? SALE_CONDITION_LABELS[receipt.sale_condition] : '-'}</Td>
                  <Td>{paymentMethodLabel(receipt.payment_method)}</Td>
                  <Td>{formatValue(receipt.value)}</Td>
                  <Td>{formatPrize(receipt.prize)}</Td>
                  <Td>
                    {!receipt.prize ? (
                      '-'
                    ) : permissions.editPrizePaymentDate ? (
                      <DateBlurInput
                        initialValue={receipt.prize_payment_date || ''}
                        ariaLabel="Data de pagamento do prêmio"
                        onSave={(value) => handlePrizePaymentDateUpdate(receipt, value)}
                      />
                    ) : receipt.prize_payment_date ? (
                      formatIsoDate(receipt.prize_payment_date)
                    ) : (
                      '-'
                    )}
                  </Td>
                  <Td>
                    <ActionButton action="view" label="Visualizar" onClick={() => setViewingId(receipt.id)} />
                    {canEditReceipt(receipt) && <ActionButton action="edit" onClick={() => setEditingId(receipt.id)} />}
                    {permissions.delete && (
                      <ActionButton action="delete" onClick={() => setDeleteDialog({ open: true, id: receipt.id })} />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(p: number) => setPage(p)}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />

      <SalesReceiptViewModal id={viewingId} onClose={() => setViewingId(null)} />
      <SalesReceiptEditModal
        id={editingId}
        onClose={(updated) => {
          setEditingId(null)
          if (updated) fetchReceipts()
        }}
      />
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        onConfirm={handleDelete}
        action="delete"
        entity="comprovante de venda"
      />

      <ActionFab label="Adicionar comprovante de venda" to="/comprovantes-de-vendas/enviar" openInNewTab />
    </>
  )
}

export default SalesReceiptsPage
